import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Bot, User, AlertTriangle, Sparkles, RotateCcw,
  History, X, Plus, MessageSquare, ChevronRight,
} from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import {
  loadChatSessions, loadChatSession,
  createChatSession, updateChatSession, deleteChatSession,
} from '../lib/userData';

// API calls go through /api/chat (Vercel serverless function).
// The Anthropic API key lives in Vercel's server-side environment — never in the browser.

// ── Build system prompt with user's financial context ────────────────────────
function buildSystemPrompt({ userProfile, netWorthData, budgetData, goalsData, retirementData }) {
  const assets      = netWorthData?.assets       || [];
  const liabilities = netWorthData?.liabilities  || [];
  const categories  = budgetData?.categories     || [];
  const goals       = goalsData                  || [];

  const totalAssets = assets.reduce((s, a) => s + (a.value || 0), 0);
  const totalLiab   = liabilities.reduce((s, l) => s + (l.value || 0), 0);
  const netWorth    = totalAssets - totalLiab;
  const totalSpent  = categories.reduce((s, c) => s + (c.spent || 0), 0);
  const income      = budgetData?.income || 0;

  const fmtUSD = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const snapshot = `
USER FINANCIAL PROFILE (as of ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}):
- Name: ${userProfile?.name || 'User'}
- Age: ${userProfile?.age || 'Unknown'}
- Target Retirement Age: ${userProfile?.retirementAge || 65}
- Risk Tolerance: ${userProfile?.riskTolerance || 'moderate'}
- Employment: ${userProfile?.employmentStatus || 'Unknown'}
- Tax Filing: ${userProfile?.taxFilingStatus || 'single'}

NET WORTH:
- Total Assets: ${fmtUSD(totalAssets)}
- Total Liabilities: ${fmtUSD(totalLiab)}
- Net Worth: ${fmtUSD(netWorth)}
${assets.length > 0 ? `- Assets: ${assets.map(a => `${a.name} (${fmtUSD(a.value)})`).join(', ')}` : '- No assets added yet'}
${liabilities.length > 0 ? `- Debts: ${liabilities.map(l => `${l.name} (${fmtUSD(l.value)})`).join(', ')}` : '- No liabilities added'}

BUDGET (Monthly):
- Income: ${fmtUSD(income)}
- Total Spent: ${fmtUSD(totalSpent)}
- Savings: ${fmtUSD(income - totalSpent)}
- Savings Rate: ${income > 0 ? Math.round(((income - totalSpent) / income) * 100) : 0}%
${categories.length > 0
    ? `- Spending by Category:\n${categories.map(c => `  • ${c.name}: ${fmtUSD(c.spent || 0)} / ${fmtUSD(c.budgeted)} budgeted`).join('\n')}`
    : '- No budget categories set up yet'}

FINANCIAL GOALS:
${goals.length > 0
    ? goals.map(g => `- ${g.icon || ''} ${g.name}: ${fmtUSD(g.current || 0)} saved toward ${fmtUSD(g.target || 0)} (${Math.round(((g.current || 0) / (g.target || 1)) * 100)}%)`).join('\n')
    : '- No goals set yet'}

RETIREMENT:
- Monte Carlo Success Rate: ${retirementData?.successRate || 0}%
- Projected at Retirement: ${fmtUSD(retirementData?.projectedAt65 || 0)}
`.trim();

  return `You are Finovo's AI Financial Advisor — a knowledgeable, empathetic, and practical financial advisor embedded in the Finovo personal finance app.

Your role is to provide personalized financial advice based on the user's actual financial data. Always reference specific numbers from their profile when relevant.

${snapshot}

GUIDELINES:
- Be warm, encouraging, and clear. Avoid jargon unless you explain it.
- Reference the user's actual data (their specific balances, goals, spending) to make advice personal.
- For investment advice, always clarify you are an AI and recommend consulting a licensed financial advisor for major decisions.
- Focus on actionable next steps. Keep responses concise but thorough.
- Use markdown formatting (bullet points, bold) to structure longer responses.
- You can help with: budgeting tips, debt payoff strategy, retirement planning, savings goals, investing basics, tax considerations, and general financial wellness.
- If the user's data is incomplete (e.g., no budget set up), encourage them to add that data first.
- NEVER fabricate specific investment recommendations for specific stocks or funds.`;
}

// ── Single message bubble ─────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-blue-600' : 'bg-gradient-to-br from-purple-600 to-blue-600'
      }`}>
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-white" />}
      </div>
      <div className={`max-w-[82%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-blue-600 text-white rounded-tr-sm'
            : 'bg-slate-800 border border-slate-700 text-slate-200 rounded-tl-sm'
        }`}>
          {isUser ? msg.content : <MarkdownText text={msg.content} />}
        </div>
        {msg.ts && (
          <span className="text-xs text-slate-600 px-1">
            {new Date(msg.ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
      </div>
    </div>
  );
}

// Minimal markdown renderer for bold, bullets
function MarkdownText({ text }) {
  const lines = text.split('\n');
  return (
    <span>
      {lines.map((line, i) => {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const rendered = parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j} className="font-semibold text-slate-100">{part}</strong> : part
        );
        const isBullet = line.trim().startsWith('- ') || line.trim().startsWith('• ');
        if (isBullet) {
          return (
            <span key={i} className="flex items-start gap-2 mt-1">
              <span className="text-blue-400 flex-shrink-0 mt-0.5">•</span>
              <span>{rendered.slice(1)}</span>
              {'\n'}
            </span>
          );
        }
        return <span key={i}>{rendered}{i < lines.length - 1 ? '\n' : ''}</span>;
      })}
    </span>
  );
}

// ── Suggested prompts ─────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "How can I improve my savings rate?",
  "Am I on track for retirement?",
  "What's the best way to pay off my debt?",
  "How should I allocate my investments?",
  "Review my budget and give me tips",
  "What's my biggest financial risk right now?",
];

// ── Main component ────────────────────────────────────────────────────────────
export default function AIAdvisor() {
  const {
    userId, userProfile,
    netWorthData, budgetData, goalsData, retirementData,
  } = useUserData();

  // ── Current live chat ─────────────────────────────────────────────────────
  const [messages,         setMessages]         = useState([]);   // always starts EMPTY
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [input,            setInput]            = useState('');
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState(null);

  // ── History sidebar ───────────────────────────────────────────────────────
  const [showHistory,     setShowHistory]     = useState(false);
  const [sessions,        setSessions]        = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [viewingSession,  setViewingSession]  = useState(null); // null → show live chat

  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);

  // Load session list once userId is available
  useEffect(() => {
    if (!userId) return;
    setSessionsLoading(true);
    loadChatSessions(userId).then(list => {
      setSessions(list);
      setSessionsLoading(false);
    });
  }, [userId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, viewingSession, loading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const startNewChat = useCallback(() => {
    setMessages([]);
    setCurrentSessionId(null);
    setViewingSession(null);
    setError(null);
    setShowHistory(false);
  }, []);

  const viewPastSession = useCallback(async (session) => {
    const full = await loadChatSession(session.id);
    setViewingSession({ ...session, messages: full?.messages || [] });
    setShowHistory(false);
  }, []);

  const handleDeleteSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await deleteChatSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (viewingSession?.id === sessionId) setViewingSession(null);
      if (currentSessionId === sessionId) {
        setMessages([]);
        setCurrentSessionId(null);
      }
    } catch (err) {
      console.error('[AIAdvisor] delete error:', err);
    }
  }, [viewingSession, currentSessionId]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    setError(null);
    setInput('');
    setViewingSession(null); // exit read-only mode if the user was browsing history

    const prevMessages = messages; // snapshot for rollback
    const userMsg      = { role: 'user', content: text.trim(), ts: Date.now() };
    const withUser     = [...messages, userMsg];
    setMessages(withUser);

    setLoading(true);
    try {
      const systemPrompt = buildSystemPrompt({ userProfile, netWorthData, budgetData, goalsData, retirementData });

      // Send last 20 messages as context (token budget)
      const apiMessages = withUser.slice(-20).map(m => ({ role: m.role, content: m.content }));

      const res = await fetch('/api/chat', {
        method:  'POST',
        headers: { 'content-type': 'application/json' },
        body:    JSON.stringify({ messages: apiMessages, systemPrompt }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Server error ${res.status}`);
      }

      const data             = await res.json();
      const assistantContent = data.content?.[0]?.text || 'Sorry, I could not generate a response.';
      const assistantMsg     = { role: 'assistant', content: assistantContent, ts: Date.now() };
      const finalMessages    = [...withUser, assistantMsg];
      setMessages(finalMessages);

      // ── Persist to Supabase (best-effort — chat still works if this fails) ──
      if (userId) {
        try {
          if (currentSessionId) {
            // Update existing session
            await updateChatSession(currentSessionId, finalMessages);
            setSessions(prev => prev.map(s =>
              s.id === currentSessionId
                ? { ...s, updated_at: new Date().toISOString() }
                : s
            ));
          } else {
            // First exchange in a brand-new session → auto-title from first user message
            const title   = text.trim().slice(0, 60) + (text.trim().length > 60 ? '…' : '');
            const session = await createChatSession(userId, title, finalMessages);
            setCurrentSessionId(session.id);
            setSessions(prev => [{
              id: session.id,
              conversation_title: title,
              created_at:  session.created_at,
              updated_at:  session.updated_at,
            }, ...prev]);
          }
        } catch (saveErr) {
          // Don't surface this — chat functionality is unaffected
          console.error('[AIAdvisor] session save error:', saveErr);
        }
      }
    } catch (err) {
      console.error('[AIAdvisor] error:', err);
      setError(err.message);
      setMessages(prevMessages); // rollback optimistic user message
    } finally {
      setLoading(false);
    }
  }, [loading, messages, currentSessionId, userId, userProfile, netWorthData, budgetData, goalsData, retirementData]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const isKeyMissing = error && error.includes('ANTHROPIC_API_KEY');

  // Which messages to render (read-only view vs. live chat)
  const displayMessages = viewingSession ? viewingSession.messages : messages;

  return (
    <div className="flex h-full max-h-screen overflow-hidden">

      {/* ── History sidebar ─────────────────────────────────────────────────── */}
      {showHistory && (
        <div className="w-72 flex-shrink-0 border-r border-slate-800 flex flex-col bg-slate-900/60">
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-slate-200">Chat History</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={startNewChat}
                title="New conversation"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              >
                <Plus size={15} />
              </button>
              <button
                onClick={() => setShowHistory(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto py-2">
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <MessageSquare size={22} className="text-slate-700 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No past conversations yet</p>
              </div>
            ) : (
              <div className="space-y-0.5 px-2">
                {sessions.map(s => (
                  <div
                    key={s.id}
                    onClick={() => viewPastSession(s)}
                    className={`group flex items-start gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      viewingSession?.id === s.id
                        ? 'bg-blue-600/20 border border-blue-600/30'
                        : 'hover:bg-slate-800'
                    }`}
                  >
                    <MessageSquare size={13} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs truncate leading-snug ${
                        viewingSession?.id === s.id ? 'text-blue-300' : 'text-slate-300'
                      }`}>
                        {s.conversation_title}
                      </p>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        {new Date(s.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteSession(s.id, e)}
                      title="Delete conversation"
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-600 hover:text-rose-400 transition-all flex-shrink-0 mt-0.5"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Main chat panel ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-900/30">
              <Sparkles size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-100">AI Financial Advisor</h1>
              <p className="text-xs text-slate-500">Personalized advice from your financial data</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* New Chat */}
            <button
              onClick={startNewChat}
              title="Start a new conversation"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <Plus size={13} /> New Chat
            </button>

            {/* History toggle */}
            <button
              onClick={() => setShowHistory(v => !v)}
              title="Browse past conversations"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                showHistory
                  ? 'bg-slate-700 text-slate-200'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
            >
              <History size={13} />
              History
              {sessions.length > 0 && (
                <span className="bg-slate-600 text-slate-300 text-[10px] leading-none rounded-full px-1.5 py-0.5 ml-0.5">
                  {sessions.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Read-only banner when viewing a past session */}
        {viewingSession && (
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-2 bg-blue-950/40 border-b border-blue-800/30">
            <div className="flex items-center gap-2 min-w-0">
              <History size={12} className="text-blue-400 flex-shrink-0" />
              <span className="text-xs font-medium text-blue-300 truncate">
                {viewingSession.conversation_title}
              </span>
              <span className="text-blue-700 flex-shrink-0">·</span>
              <span className="text-[11px] text-blue-500 flex-shrink-0">
                {new Date(viewingSession.updated_at || viewingSession.created_at).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </span>
              <span className="text-[11px] text-blue-700 flex-shrink-0 ml-1">Read-only</span>
            </div>
            <button
              onClick={() => setViewingSession(null)}
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-200 flex-shrink-0 ml-3 transition-colors"
            >
              <ChevronRight size={12} /> Back to chat
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {displayMessages.length === 0 && !viewingSession ? (
            /* Welcome / empty state */
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600/30 to-blue-600/30 border border-purple-700/40 rounded-2xl flex items-center justify-center mb-4">
                <Sparkles size={28} className="text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-200 mb-2">Your AI Financial Advisor</h2>
              <p className="text-slate-400 text-sm max-w-md leading-relaxed mb-6">
                Ask me anything about your finances. I have full context of your budget, net worth, goals, and retirement plan to give you personalized advice.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-left px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl text-xs text-slate-300 hover:text-slate-200 transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {displayMessages.map((msg, i) => (
                <MessageBubble key={`${viewingSession?.id || 'live'}-${i}`} msg={msg} />
              ))}

              {/* Typing indicator */}
              {!viewingSession && loading && (
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Bot size={14} className="text-white" />
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex gap-1.5 items-center">
                      {[0, 1, 2].map(j => (
                        <div
                          key={j}
                          className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${j * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Error display */}
              {!viewingSession && error && (
                isKeyMissing ? (
                  <div className="bg-amber-950/40 border border-amber-700/50 rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <h3 className="font-semibold text-amber-300 mb-1 text-sm">API Key Not Configured</h3>
                        <p className="text-slate-400 text-xs leading-relaxed mb-3">
                          The AI Advisor requires an Anthropic API key set on the server. Add it in your Vercel project settings:
                        </p>
                        <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 font-mono text-xs text-slate-300 mb-3 space-y-0.5">
                          <p className="text-slate-500"># Vercel Dashboard → Project → Settings → Environment Variables</p>
                          <p>ANTHROPIC_API_KEY = sk-ant-...</p>
                        </div>
                        <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
                          <li>Get your key at <span className="text-blue-400">console.anthropic.com</span></li>
                          <li>Add <code className="bg-slate-800 px-1 rounded">ANTHROPIC_API_KEY</code> in Vercel env vars</li>
                          <li>Redeploy, or use <code className="bg-slate-800 px-1 rounded">.env.local</code> for local dev</li>
                        </ol>
                        <button onClick={() => setError(null)} className="mt-3 text-xs text-slate-600 hover:text-slate-400 transition-colors">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-rose-950/40 border border-rose-800/50 rounded-xl text-xs text-rose-300">
                    <AlertTriangle size={13} />
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="ml-auto hover:text-rose-200">
                      <RotateCcw size={12} />
                    </button>
                  </div>
                )
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* ── Input area (live chat only) ──────────────────────────────────── */}
        {!viewingSession && (
          <div className="flex-shrink-0 border-t border-slate-800 px-4 sm:px-6 py-3">
            {/* Contextual suggestion chips every 6 messages */}
            {messages.length > 0 && messages.length % 6 === 0 && !loading && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {SUGGESTIONS.slice(0, 3).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-400 hover:text-slate-200 hover:border-slate-600 transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-3 bg-slate-800 border border-slate-700 rounded-2xl px-4 py-2.5 focus-within:border-blue-500 transition-colors">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your finances… (Enter to send, Shift+Enter for new line)"
                rows={1}
                className="flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-600 resize-none outline-none leading-relaxed"
                style={{ minHeight: '24px', maxHeight: '120px' }}
                disabled={loading}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className={`p-1.5 rounded-xl transition-all flex-shrink-0 ${
                  input.trim() && !loading
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30'
                    : 'text-slate-600 cursor-not-allowed'
                }`}
              >
                <Send size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-700 text-center mt-1.5">
              AI advice is educational only — consult a licensed advisor for major decisions.
            </p>
          </div>
        )}

        {/* ── Footer when viewing a past session ──────────────────────────── */}
        {viewingSession && (
          <div className="flex-shrink-0 border-t border-slate-800 px-4 sm:px-6 py-3 flex items-center justify-center gap-3">
            <button
              onClick={() => setViewingSession(null)}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-sm text-slate-300 hover:text-slate-100 transition-colors"
            >
              <ChevronRight size={14} /> Back to Current Chat
            </button>
            <button
              onClick={startNewChat}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-sm text-white transition-colors"
            >
              <Plus size={14} /> New Chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
